import { Box } from "@chakra-ui/react";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";

import { useCustomVariation } from "@/hooks/useCustomVariation";
import { getParlorInfoByRegionCode } from "@/utils/getRegionParlorInfo";
import {
  buildRegionCostFrameCopy,
  markRegionPageUnit,
  RegionPageUnit,
  resolveRegionCostFrameKey,
} from "@/utils/regionCostFrameExperiment";
import { formatRegionParlorPickLabel, markRegionParlorPick } from "@/utils/regionParlorPickContext";

import {
  colors,
  customEvent,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  getCdnImageSrc,
  HStack,
  Image,
  Spacing,
} from "goi_common";
import { baseColors } from "goi_common/src/design-system";

import MubinsoQuickCard from "./MubinsoQuickCard";
import RegionCostFrameFixedCta from "./RegionCostFrameFixedCta";
import RegionCostFrameParlorSheet from "./RegionCostFrameParlorSheet";

/**
 * 배정이 안 떨어져도 히어로를 내보내는 한계 시간(ms).
 *
 * Hackle SDK 가 막힌 환경(광고 차단, 스크립트 실패)에서 화면이 영원히 비지 않게 한다.
 * 이 값을 지나 A 로 확정되면 그때 한 번 나타난다. 짧게 잡으면 느린 네트워크에서 A 가
 * 먼저 보였다가 B 로 바뀌는 플리커가 되돌아온다.
 */
const VARIATION_FALLBACK_MS = 1500;

interface RegionCostFrameHeroProps {
  /** 폼 기본값. 페이지가 가진 지역 코드 그대로 */
  defaultSido: number | null;
  defaultSigungu: number | null;
  /** B·C안 제목에 박을 지역명. 시군구면 "경기 평택시", 시도면 "경기" */
  regionLabel: string;
  /**
   * 히어로 아래에 붙던 오렌지 배너(`RegionCostCard`). **A안에서만** 렌더한다.
   *
   * 호출부에서 만들어 받는 이유는 배너가 sido/sigungu 문자열을 따로 받기 때문이다.
   * B·C 는 히어로가 이미 비용을 약속하고(B), 폼 안에 2갈래가 들어가(C) 배너와 말이 겹친다.
   * 그 배너의 방문 대비 제출은 0.64~1.60% 라 잃는 양도 작다.
   */
  costCard?: ReactNode;
}

/**
 * 지역 페이지 히어로 폼. 비용 프레임 A/B/C (`regionCostFrameExperiment`) 적용 지점.
 *
 * | 안 | 히어로 | 오렌지 배너 | 폼 |
 * |---|---|---|---|
 * | A | 현행 "내 주변 장례식장 한번에 알아보기" | 유지 | 현행 |
 * | B | 비용 프레임 카피 + 절감 근거 | 제거 | 현행 |
 * | C | B 와 같음 | 제거 | 2갈래 탭 + 장례식장 선택 |
 *
 * 왜 2단(outer/assigned)인가
 * - 실험 키가 0(콘솔 미설정)이면 훅을 아예 호출하지 않아야 한다. 키 0 으로 `useVariation` 을
 *   부르면 SDK 가 노출을 어떻게 기록하는지 확정할 수 없어서, 코드베이스가 쓰는 방식
 *   (`shouldMountMubinsoRegionExperiment`, `ParlorVisitedListSlot`)대로 마운트를 가드한다.
 * - 훅은 조건부 호출이 안 되므로 호출부를 컴포넌트로 분리한다.
 *
 * 왜 `HackleExperimentContainer` 를 안 쓰는가
 * - 그 컨테이너는 배정 전 `null` 을 반환한다. 로그인 화면에서는 괜찮지만 이 페이지는 SSG 고
 *   여기가 히어로라, 정적 HTML 에 히어로가 통째로 빠지고 LCP·CLS 가 깨진다.
 * - 대신 배정 전에는 A 마크업으로 **자리만 잡고 숨긴다**(`visibility: hidden`). 높이가 유지돼
 *   아래 콘텐츠가 밀리지 않고, 확정 전 A 를 보여줬다가 B 로 갈아끼우는 플리커도 없다.
 *   자세한 근거는 `AssignedHero` 의 `isSettled` 주석 참고.
 *
 * 하이드레이션
 * - 지면 종류는 props 로 결정돼 서버와 클라이언트가 같은 값을 본다. 광고 파라미터처럼
 *   서버에서 못 보는 값에 의존하지 않으므로 마크업이 어긋날 여지가 없다.
 */
export default function RegionCostFrameHero({
  defaultSido,
  defaultSigungu,
  regionLabel,
  costCard,
}: RegionCostFrameHeroProps) {
  const unit: RegionPageUnit = defaultSigungu ? "sigungu" : "sido";

  /*
   * 지면 종류를 세션에 남긴다. 로그인 화면은 어느 지면에서 왔는지 모르고, 폼에서 고른 지역은
   * 지면 종류가 아니다(시도 페이지에서 시군구를 고른 사람이 34%). 카드와 로그인이 같은 키로
   * 묶이려면 지면이 자기 종류를 직접 남겨야 한다.
   */
  useEffect(() => {
    markRegionPageUnit(unit);
  }, [unit]);

  const experimentKey = resolveRegionCostFrameKey(unit);
  const controlHero = (
    <>
      <MubinsoQuickCard
        variant="hero"
        purpose="general"
        defaultSido={defaultSido}
        defaultSigungu={defaultSigungu}
        loginEventFrom="uv_rgn_form"
        regionQuoteServiceKey={null}
      />
      {costCard && (
        <>
          <Spacing gap={24} />
          {costCard}
        </>
      )}
    </>
  );

  if (experimentKey <= 0) return controlHero;

  return (
    <AssignedHero
      experimentKey={experimentKey}
      unit={unit}
      regionLabel={regionLabel}
      defaultSido={defaultSido}
      defaultSigungu={defaultSigungu}
      controlHero={controlHero}
    />
  );
}

function AssignedHero({
  experimentKey,
  unit,
  regionLabel,
  defaultSido,
  defaultSigungu,
  controlHero,
}: RegionCostFrameHeroProps & {
  experimentKey: number;
  unit: RegionPageUnit;
  controlHero: JSX.Element;
}) {
  // region_unit 은 실험이 지면별로 갈려 있어 판정에는 안 쓴다. 두 실험을 나란히 볼 때 쓴다
  const variation = useCustomVariation(experimentKey, "Z", { region_unit: unit });

  /*
   * 배정이 확정됐는지. 확정 전에는 히어로를 **숨긴 채 자리만 잡는다.**
   *
   * 왜 필요한가
   * - 배정 전에 A 를 보여주고 확정 후 B 로 갈아끼우면 카드가 눈앞에서 바뀐다(플리커).
   *   `useCustomVariation` 은 dev 환경에서 첫 렌더가 **항상** "Z" 다. clientVariation 초기값이
   *   "Z" 이고 localStorage 를 effect 에서 읽기 때문이다. 운영에서도 Hackle SDK 초기화 전
   *   첫 렌더는 기본값을 받는다.
   *
   * 왜 `null` 이 아니라 숨김인가
   * - 이 페이지는 SSG 고 여기가 히어로다. 통째로 빼면 정적 HTML 에 히어로가 없어 아래 콘텐츠가
   *   위로 붙었다가 다시 밀리고, 크롤러도 빈 히어로를 본다. A 마크업으로 자리를 잡아 둔다.
   *
   * 폴백 타이머를 두는 이유
   * - "Z" 는 "아직 모름" 과 "SDK 로드 실패" 를 구분하지 못한다. 타이머가 없으면 SDK 가 막힌
   *   환경(광고 차단 등)에서 히어로가 영원히 안 보인다. 그때는 A 로 확정해 내보낸다.
   */
  const [isSettled, setIsSettled] = useState(false);

  useEffect(() => {
    if (variation !== "Z") {
      setIsSettled(true);
      return;
    }
    const timer = setTimeout(() => setIsSettled(true), VARIATION_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [variation]);

  /* ---------------------------------------------------------------------- *
   * C안 상태. B 에서는 쓰이지 않지만 훅이라 위에서 전부 호출한다
   * ---------------------------------------------------------------------- */

  /** 2갈래 탭. "total" 이 기본이다. A·B 가 약속하던 것과 같은 쪽을 먼저 보여준다 */
  const [costTab, setCostTab] = useState<"total" | "parlor">("total");
  const [pickedNames, setPickedNames] = useState<string[]>([]);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  /** 폼에서 고른 지역. 페이지 지역이 아니라 사용자가 고른 값이다 */
  const [formRegion, setFormRegion] = useState<{ sido: number | null; sigungu: number | null }>({
    sido: defaultSido,
    sigungu: defaultSigungu,
  });

  const handleRegionChange = useCallback((sido: number | null, sigungu: number | null) => {
    setFormRegion((prev) =>
      prev.sido === sido && prev.sigungu === sigungu ? prev : { sido, sigungu },
    );
  }, []);

  /*
   * 지역이 바뀌면 고른 장례식장을 비운다. 그 지역에 없는 곳이 선택으로 남으면 로그인 화면이
   * 엉뚱한 이름을 되짚는다.
   */
  useEffect(() => {
    setPickedNames([]);
  }, [formRegion.sido, formRegion.sigungu]);

  /*
   * 고른 장례식장을 세션에 반영한다. **총비용 탭에서는 비운다.**
   *
   * 탭을 장례식장에서 총비용으로 되돌린 사람에게 옛 선택이 따라붙으면, 로그인 화면이
   * 이번에 고르지 않은 장례식장을 되짚고 상담 memo 에도 그 이름이 실린다.
   *
   * 렌더 중이 아니라 effect 에서 쓰는 이유는 세션 쓰기가 부작용이기 때문이다.
   */
  useEffect(() => {
    markRegionParlorPick(costTab === "parlor" ? pickedNames : []);
  }, [pickedNames, costTab]);

  const { regionParlors, formRegionLabel } = useMemo(() => {
    if (!formRegion.sido) return { regionParlors: [], formRegionLabel: regionLabel };

    const { regionParlors: found, hasSigunguParlor } = getParlorInfoByRegionCode(
      formRegion.sido,
      formRegion.sigungu ?? undefined,
    );

    const sidoName = FUNERAL_SIDO.get(formRegion.sido) ?? "";
    // 시군구에 장례식장이 없어 시도로 fallback 된 경우 시군구명을 쓰지 않는다 (오라벨 방지)
    const sigunguName =
      hasSigunguParlor && formRegion.sigungu
        ? FUNERAL_SIGUNGU.get(formRegion.sido)?.get(formRegion.sigungu)
        : undefined;

    return {
      regionParlors: found,
      formRegionLabel: [sidoName, sigunguName].filter(Boolean).join(" ") || regionLabel,
    };
  }, [formRegion.sido, formRegion.sigungu, regionLabel]);

  /** 함수형 업데이트라 빠르게 두 번 눌러도 앞 선택이 덮이지 않는다 */
  const handlePickToggle = useCallback((name: string) => {
    setPickedNames((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : prev.concat(name),
    );
  }, []);

  if (!isSettled) {
    /*
     * 배정 전 스켈레톤.
     *
     * 앞서는 A 마크업을 `visibility: hidden` 으로 숨겼는데, 그러면 그 자리가 **그냥 빈
     * 공간**이라 페이지가 덜 그려진 것처럼 보였다. 스켈레톤은 같은 높이를 잡으면서
     * "여기에 곧 뭔가 온다" 를 말해준다.
     *
     * 자리를 비우지 않는 이유는 그대로다. 이 페이지는 SSG 고 여기가 히어로라, 통째로 빼면
     * 아래 콘텐츠가 위로 붙었다가 확정되면서 다시 밀린다.
     */
    return <HeroSkeleton />;
  }

  // 확정된 A(그리고 폴백으로 확정된 "Z")는 현행 화면 그대로다. 오렌지 배너도 여기서만 나온다
  if (variation !== "B" && variation !== "C") return controlHero;

  const copy = buildRegionCostFrameCopy(regionLabel);
  const isC = variation === "C";
  const isParlorTab = isC && costTab === "parlor";

  /* ---------------------------------------------------------------------- *
   * C안 폼 슬롯
   * ---------------------------------------------------------------------- */

  /*
   * 세그먼티드 컨트롤. 선택 표시를 **움직이는 판** 하나로 둔다.
   *
   * 앞서는 선택된 쪽만 배경을 칠했는데, 그러면 비선택 쪽이 그냥 글씨라 눌러지는 것으로
   * 보이지 않았다. 판이 좌우로 미끄러지면 "둘 중 하나를 고르는 스위치" 로 읽힌다.
   *
   * 판은 `absolute` + `translateX` 로 움직인다. 각 칸에 배경을 켜고 끄면 전환이 툭 끊긴다.
   */
  const tabIndex = costTab === "total" ? 0 : 1;

  const tabButton = (
    key: "total" | "parlor",
    label: string,
    trigger: "cost_tab-total" | "cost_tab-parlor",
  ) => {
    const on = costTab === key;
    return (
      <Box
        as="button"
        type="button"
        flex={1}
        position="relative"
        zIndex={1}
        padding="11px 6px"
        background="transparent"
        border="none"
        color={on ? colors.white : baseColors.label_alternative_1}
        className={on ? "body_2_normal_sb" : "body_2_normal_m"}
        cursor="pointer"
        transition="color 0.22s cubic-bezier(0.22, 1, 0.36, 1)"
        onClick={() => {
          customEvent({ newGtm: { path: "parlor_region", trigger_and_target: trigger } });
          setCostTab(key);
        }}
      >
        {label}
      </Box>
    );
  };

  const costTabs = (
    <Box
      position="relative"
      display="flex"
      background={baseColors.fill_normal}
      borderRadius="10px"
      padding="4px"
    >
      {/* 움직이는 판. 칸이 둘이라 너비는 "절반에서 좌우 패딩을 뺀 값" 이다 */}
      <Box
        position="absolute"
        top="4px"
        bottom="4px"
        left="4px"
        width="calc(50% - 4px)"
        borderRadius="8px"
        background={baseColors.label_normal}
        transform={`translateX(${tabIndex * 100}%)`}
        transition="transform 0.22s cubic-bezier(0.22, 1, 0.36, 1)"
        aria-hidden
      />
      {tabButton("total", copy.cost.tabTotal, "cost_tab-total")}
      {tabButton("parlor", copy.cost.tabParlor, "cost_tab-parlor")}
    </Box>
  );

  const formTopSlot = isC ? (
    <>
      <Box className="body_2_normal_sb" color={baseColors.label_normal}>
        {copy.cost.tabQuestion}
      </Box>
      <Spacing gap={8} />
      {costTabs}
      <Spacing gap={12} />
      {/* 안내 박스. 탭마다 "무엇을 안 골라도 되는지" 를 먼저 말해 부담을 지운다 */}
      <Box
        background={colors.orange50}
        border={`1px solid ${colors.orange100}`}
        borderRadius="10px"
        padding="12px"
        wordBreak="keep-all"
      >
        <Box className="body_2_normal_sb" color={baseColors.label_normal}>
          {isParlorTab ? copy.cost.guideParlorTitle : copy.cost.guideTotalTitle}
        </Box>
        <Spacing gap={4} />
        <Box className="caption_1_r" color={baseColors.label_alternative_1}>
          {isParlorTab ? copy.cost.guideParlorBody : copy.cost.guideTotalBody}
        </Box>
      </Box>
      <Spacing gap={16} />
    </>
  ) : undefined;

  /*
   * 시/군/구를 고르기 전에는 장례식장을 못 고르게 잠근다.
   *
   * 시도만 고른 상태에서 시트를 열면 그 시도 전체가 나오고(부산 60곳), 고른 장례식장과
   * 폼의 지역이 어긋난 채로 제출된다. 시군구 지면은 처음부터 채워져 있어 잠기지 않는다.
   */
  const canPickParlor = !!formRegion.sigungu;

  const formMiddleSlot = isParlorTab ? (
    <>
      <Box className="body_2_normal_sb" color={baseColors.label_normal}>
        {copy.cost.pickerQuestion}
      </Box>
      <Spacing gap={4} />
      <Box className="caption_1_r" color={baseColors.label_assistive_2} wordBreak="keep-all">
        {copy.cost.pickerHint(formRegionLabel)}
      </Box>
      <Spacing gap={8} />
      <Box
        as="button"
        type="button"
        disabled={!canPickParlor}
        width="100%"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap="8px"
        padding="11px 12px"
        borderRadius="8px"
        textAlign="left"
        background={canPickParlor ? "transparent" : baseColors.fill_normal}
        border={`1px solid ${
          canPickParlor && pickedNames.length ? baseColors.label_normal : baseColors.line_normal
        }`}
        color={
          canPickParlor && pickedNames.length
            ? baseColors.label_normal
            : baseColors.label_assistive_2
        }
        className="body_2_normal_sb"
        cursor={canPickParlor ? "pointer" : "not-allowed"}
        onClick={() => {
          if (!canPickParlor) return;
          customEvent({
            newGtm: { path: "parlor_region", trigger_and_target: "prlr_pick-opn_sheet" },
          });
          setIsSheetOpen(true);
        }}
      >
        <Box as="span" wordBreak="keep-all">
          {!canPickParlor
            ? copy.cost.pickerLocked
            : pickedNames.length
            ? formatRegionParlorPickLabel(pickedNames)
            : copy.cost.pickerPlaceholder}
        </Box>
        <Box as="span" color={baseColors.label_assistive_2} flexShrink={0}>
          ›
        </Box>
      </Box>
      <Spacing gap={16} />
    </>
  ) : undefined;

  /** 제출 버튼 문구. 탭과 선택 여부로 갈린다 */
  const submitText = !isC
    ? copy.submitText
    : isParlorTab
    ? pickedNames.length
      ? copy.cost.submitParlorPicked
      : copy.cost.submitParlorEmpty
    : copy.cost.submitTotal;

  const heroCard = (
    <MubinsoQuickCard
      variant="hero"
      purpose="general"
      defaultSido={defaultSido}
      defaultSigungu={defaultSigungu}
      loginEventFrom="uv_rgn_form"
      regionQuoteServiceKey={null}
      needs="price"
      heroBadge={copy.badge}
      heroTitle={
        <>
          {copy.titleRegion}{" "}
          {/* 이 카드가 파는 것이 "비용" 이라는 신호. A 가 "한번에" 를 강조하던 자리와 같다 */}
          <span style={{ color: baseColors.orange_400 }}>{copy.titleAccent}</span>
          <br />
          {copy.titleBottom}
        </>
      }
      heroDescription={
        <>
          {copy.descLine1}
          <br />
          {copy.descLine2Lead}
          {/* A 는 "찾아드려요"(목록)를 약속한다. 줄일 항목까지 준다는 것이 갈리는 지점이다 */}
          <span style={{ fontWeight: 700, color: colors.white }}>{copy.descLine2Accent}</span>
          {copy.descLine2Tail}
        </>
      }
      heroSocialProof={
        /*
         * 2026-07-06 이전 히어로가 쓰던 블록을 그대로 되살린다. 아이콘까지 같은 것을 쓰는
         * 이유는 같은 말을 같은 그림으로 해야 재방문자에게도 같은 약속으로 읽히기 때문이다.
         * 원본은 밝은 배경(gray50)이라 색만 다크에 맞춘다.
         */
        <HStack gap="12px" alignItems="center">
          <Image src={getCdnImageSrc(copy.socialProof.icon)} width="34px" height="34px" alt="" />
          <Box flex={1} wordBreak="keep-all">
            {copy.socialProof.lead}
            <br />
            {copy.socialProof.body}
            <span style={{ fontWeight: 700, color: colors.white }}>{copy.socialProof.amount}</span>
            {copy.socialProof.tail}
          </Box>
        </HStack>
      }
      formTopSlot={formTopSlot}
      formMiddleSlot={formMiddleSlot}
      onRegionChange={isC ? handleRegionChange : undefined}
      /*
       * 조문객은 **장례식장별 탭에서만** 묻는다.
       *
       * 총비용 탭 안내가 "희망 지역과 준비 상황만 선택해주세요" 라 조문객을 물으면 그 문구와
       * 모순된다. 장례식장별 탭 안내에는 "조문객 수에 따라 빈소, 음식 비용이 크게 달라져요"
       * 가 있어 묻는 이유가 화면에 적혀 있다.
       *
       * 준비 상황 드롭다운은 C 전체에 건다. 탭을 오갈 때 폼 구조가 바뀌면 같은 화면이
       * 두 번 다시 그려지는 것처럼 보인다.
       *
       * B 는 "폼 구조는 A 와 같고 약속만 다르다" 가 정의라 둘 다 건드리지 않는다.
       */
      showCondolers={isParlorTab}
      statusAsSelect={isC}
      /*
       * 비어 있는 항목을 그 자리에서 짚어 준다. 버튼 위 한 줄은 무엇을 더 해야 하는지는
       * 알려주지만 어디를 눌러야 하는지는 알려주지 못한다.
       *
       * C 에만 켠다. A 는 대조군이라 화면이 바뀌면 안 되고, B 는 "폼 구조는 A 와 같고
       * 약속만 다르다" 가 정의다.
       */
      inlineHints={isC}
      /*
       * 고른 장례식장을 **견적 memo** 에도 싣는다.
       *
       * `handleSangjoQuotationRequest` 가 제출 직후 `PATCH /sangjo_quotation_requests/{id}/` 로
       * memo 를 보낸다. 이 값은 슬랙 알림의 "메모(설문 응답)" 줄과 견적 상세에 남아, 상담사가
       * 나중에 다시 열어도 보인다. 로그인 memo 는 슬랙 알림에만 남고 견적에는 안 붙는다.
       *
       * 개별 장례식장 견적(`ParlorRequestQuestionStepByStep`)이 "선택한 평형" 을 담는 자리와 같다.
       */
      memo={pickedNames.length ? `관심 장례식장: ${pickedNames.join(", ")}` : undefined}
      submitText={submitText}
      submitBorderRadius={copy.submitBorderRadius}
    />
  );

  return (
    <>
      <RegionCostFrameFixedCta label={copy.fixedCtaText}>{heroCard}</RegionCostFrameFixedCta>
      {isC && (
        <RegionCostFrameParlorSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          parlors={regionParlors as never}
          selectedNames={pickedNames}
          onToggle={handlePickToggle}
          regionLabel={formRegionLabel}
          copy={copy.cost}
        />
      )}
    </>
  );
}

/**
 * 배정 전 히어로 자리를 채우는 스켈레톤.
 *
 * 높이를 A·B·C 히어로의 대략치에 맞춘다. 정확히 같을 수는 없다(세 안의 높이가 서로 다르다).
 * 확정 시 한 번 움직이는 것은 받아들이고, **빈 공간으로 보이지 않는 것**을 택했다.
 *
 * 실제 히어로와 같은 다크 카드 위에 옅은 블록을 얹는다. 흰 스켈레톤을 쓰면 확정 순간
 * 배경색까지 바뀌어 더 크게 움직이는 것처럼 보인다.
 */
function HeroSkeleton() {
  const bar = (width: string, height: string, opacity: number) => (
    <Box
      width={width}
      height={height}
      borderRadius="6px"
      background={`rgba(255, 255, 255, ${opacity})`}
    />
  );

  return (
    <Box
      width="100%"
      background={baseColors.label_normal}
      borderRadius="20px"
      padding="20px 16px"
      aria-hidden
      css={{
        "@keyframes goiRegionHeroPulse": {
          "0%, 100%": { opacity: 0.55 },
          "50%": { opacity: 1 },
        },
        animation: "goiRegionHeroPulse 1.4s ease-in-out infinite",
      }}
    >
      {bar("72px", "24px", 0.18)}
      <Spacing gap={14} />
      {bar("78%", "26px", 0.14)}
      <Spacing gap={8} />
      {bar("56%", "26px", 0.14)}
      <Spacing gap={14} />
      {bar("92%", "14px", 0.1)}
      <Spacing gap={6} />
      {bar("70%", "14px", 0.1)}
      <Spacing gap={20} />
      {/* 흰 폼 영역 */}
      <Box background="rgba(255, 255, 255, 0.92)" borderRadius="14px" padding="16px 14px">
        <Box width="45%" height="16px" borderRadius="6px" background={baseColors.fill_normal} />
        <Spacing gap={12} />
        <Box display="flex" gap="12px">
          <Box flex={1} height="44px" borderRadius="8px" background={baseColors.fill_normal} />
          <Box flex={1} height="44px" borderRadius="8px" background={baseColors.fill_normal} />
        </Box>
        <Spacing gap={16} />
        <Box width="45%" height="16px" borderRadius="6px" background={baseColors.fill_normal} />
        <Spacing gap={12} />
        <Box height="48px" borderRadius="8px" background={baseColors.fill_normal} />
        <Spacing gap={8} />
        <Box height="48px" borderRadius="8px" background={baseColors.fill_normal} />
        <Spacing gap={16} />
        <Box height="52px" borderRadius="10px" background={baseColors.fill_normal} />
      </Box>
    </Box>
  );
}
